import {
  AltiusRes,
  IPaginationResponse,
  IPermissionItem,
  IUserManageData,
  IUserManageInvestmentEntity,
  ManageTeam
} from "store/types";
import { InvestEntity, IUserManage, IUserManageGroup, IUsersManageQueries } from "store/types";
import { delay } from "utils/delay";

import { AsyncActions } from "../base";

export interface IUsersActions {
  getUsersAction: (params: IUsersManageQueries) => Promise<IPaginationResponse<IUserManage>>;
  createUserManageAction: (data: IUserManageData) => Promise<void>;
  updateUserManageAction: (data: IUserManageData) => Promise<void>;
  deleteUserManageAction: (id: number) => Promise<void>;
  createInviteUserManageAction: (id: number) => Promise<void>;
  getInvestEntitiesAction: () => Promise<Array<InvestEntity>>;
  getUserAction: (id: number) => Promise<IUserManage>;
  getGroupsAction: () => Promise<IUserManageGroup[]>;
  createGroupAction: (name: string) => Promise<void>;
  updateGroupAction: (id: number, name: string) => Promise<void>;
  deleteGroupAction: (id: number) => Promise<void>;
  getManageTeamsAction: () => Promise<Array<ManageTeam>>;
  getUserPermissionListAction: () => Promise<IPermissionItem[]>;
  getInvestmentEntitiesAction: () => Promise<IUserManageInvestmentEntity[]>;
  getContactAction: (id: number) => Promise<IUserManage>;
  deleteContactAction: (id: number) => Promise<void>;
  updateContactAction: (params: IUserManageData) => Promise<void>;
}

export class UsersActions extends AsyncActions implements IUsersActions {
  public getUsersAction: IUsersActions["getUsersAction"] = async (params) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPaginationResponse<IUserManage>>>({
      url: "/api/v0.0.3/user-management/users",
      signal: this.addProcess("getUsersAction").signal,
      params: {
        ...params
      }
    });
    this.removeProcess("getUsersAction");
    return data;
  };

  public createUserManageAction: IUsersActions["createUserManageAction"] = async (params) => {
    await this.api.post<AltiusRes<IUserManage>>({
      url: "/api/v0.0.3/user-management/users",
      signal: this.addProcess("createUserManageAction").signal,
      data: {
        ...params
      }
    });
    this.removeProcess("createUserManageAction");
  };

  public updateUserManageAction: IUsersActions["updateUserManageAction"] = async (params) => {
    await this.api.patch<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/users/${params.id}`,
      signal: this.addProcess("updateUserManageAction").signal,
      data: {
        ...params
      }
    });
    this.removeProcess("updateUserManageAction");
  };

  public deleteUserManageAction: IUsersActions["deleteUserManageAction"] = async (id) => {
    await this.api.delete<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/users/${id}`
    });
  };

  public createInviteUserManageAction: IUsersActions["createInviteUserManageAction"] = async (id) => {
    await this.api.post<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/users/${id}/resend-invite`
    });
  };

  public getUserPermissionListAction: IUsersActions["getUserPermissionListAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPermissionItem[]>>({
      url: "/api/v0.0.3/user-management/users-permissions"
    });
    return data;
  };

  public getInvestmentEntitiesAction: IUsersActions["getInvestmentEntitiesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUserManageInvestmentEntity[]>>({
      url: "/api/v0.0.3/user-management/investment-entities"
    });
    return data;
  };

  public getInvestEntitiesAction: IUsersActions["getInvestEntitiesAction"] = async () => {
    await delay(500);

    const result: InvestEntity[] = new Array(40).fill("").map((_, index) => ({
      id: index + 1,
      deal_name: `Deal name ${index + 1}`,
      parent_entities: ["First", "Second", "Third", "Fourth"],
      subordinated_entities: ["First", "Second", "Third", "Fourth"],
      users: new Array(10).fill("").map((_, index) => ({
        id: index + 1,
        profile_photo_url:
          "https://altius-staging-files.s3.eu-west-2.amazonaws.com/accounts/fo1.altius.finance/users/profiles/100/McYWKD8Mb8h0u00kzgdTGdyaKfvQm6fY0BozBYVc.png?X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=AKIASPLFPN3DI2EMM4GZ%2F20240423%2Feu-west-2%2Fs3%2Faws4_request&X-Amz-Date=20240423T102615Z&X-Amz-SignedHeaders=host&X-Amz-Expires=300&X-Amz-Signature=e0dac0dc8ef511e5904d64851b4c46faf50dea3863be1d0b580aae1100570e20",
        role_id: 1,
        account_id: 1,
        full_name: "John Snow"
      }))
    }));
    return result;
  };

  public getUserAction: IUsersActions["getUserAction"] = async (id: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/users/${id}`,
      signal: this.addProcess("getUserAction").signal
    });
    this.removeProcess("getUserAction");
    return data;
  };

  public getGroupsAction: IUsersActions["getGroupsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUserManageGroup[]>>({
      url: "/api/v0.0.3/user-management/groups",
      signal: this.addProcess("getGroupsAction").signal
    });
    this.removeProcess("getGroupsAction");
    return data;
  };

  public createGroupAction: IUsersActions["createGroupAction"] = async (name: string) => {
    await this.api.post({
      url: "/api/v0.0.3/user-management/groups",
      data: {
        name
      },
      signal: this.addProcess("createGroupAction").signal
    });
    this.removeProcess("createGroupAction");
  };

  public updateGroupAction: IUsersActions["updateGroupAction"] = async (id: number, name: string) => {
    await this.api.post({
      url: `/api/v0.0.3/user-management/groups/${id}`,
      data: {
        _method: "PATCH",
        name
      },
      signal: this.addProcess("updateGroupAction").signal
    });
    this.removeProcess("updateGroupAction");
  };

  public deleteGroupAction: IUsersActions["deleteGroupAction"] = async (id: number) => {
    this.api.delete({
      url: `/api/v0.0.3/user-management/groups/${id}`,
      signal: this.addProcess("deleteGroupAction").signal
    });
    this.removeProcess("deleteGroupAction");
  };

  public getManageTeamsAction: IUsersActions["getManageTeamsAction"] = async () => {
    await delay(500);

    const result: ManageTeam[] = new Array(40).fill("").map((_, index) => ({
      id: index + 1,
      team_name: `Team ${index + 1}`,
      users: new Array(10).fill("").map((_, index) => ({
        id: index + 1,
        profile_photo_url:
          "https://altius-staging-files.s3.eu-west-2.amazonaws.com/accounts/fo1.altius.finance/users/profiles/100/McYWKD8Mb8h0u00kzgdTGdyaKfvQm6fY0BozBYVc.png?X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=AKIASPLFPN3DI2EMM4GZ%2F20240423%2Feu-west-2%2Fs3%2Faws4_request&X-Amz-Date=20240423T102615Z&X-Amz-SignedHeaders=host&X-Amz-Expires=300&X-Amz-Signature=e0dac0dc8ef511e5904d64851b4c46faf50dea3863be1d0b580aae1100570e20",
        role_id: index % 2 === 0 ? 1 : 2,
        account_id: 1,
        full_name: `${index + 1}John Snow`,
        email: `${index + 1}JohnSnow@gmail.com`
      })),
      deals: new Array(15).fill("").map(() => ({
        id: index + 1,
        deal_name: `${index}test name`,
        parent_deal: `${index}parent deal`,
        sub_deals: [`${index}test name`, "subDeals2", "subDeals3"],
        other_teams: [`${index}test name`, "team2", "team3"]
      })),
      note: `${index + 1}some string`
    }));
    return result;
  };

  public getContactAction: IUsersActions["getContactAction"] = async (id: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/contacts/${id}`
    });
    return data;
  };

  public deleteContactAction: IUsersActions["deleteUserManageAction"] = async (id) => {
    await this.api.delete<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/contacts/${id}`
    });
  };

  public updateContactAction: IUsersActions["updateUserManageAction"] = async (params) => {
    await this.api.patch<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/contacts/${params.id}`,
      data: {
        ...params
      }
    });
  };
}
